/**
 * Create a standalone draft invoice with an explicit payer and no work order (#2427).
 * Posts to POST /invoices. Does not invent a booking.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  DialogContent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useCompanies, type Company } from '@/hooks/useCompanies';
import { useDebounce } from '@/hooks/useDebounce';
import {
  useCreateStandaloneInvoice,
  type InvoicePayerPartyType,
} from '@/hooks/useInvoices';
import { useNotify } from '@/hooks/useNotify';
import { usePersons, type Person } from '@/hooks/usePersons';
import { DEFAULT_VAT_PERCENT } from '@/utils/vat';
import {
  buildStandaloneInvoiceBody,
  buildStandaloneInvoicePayer,
  standaloneInvoiceValidationKey,
  translateCreateStandaloneError,
} from './createStandaloneInvoiceForm';
import { InvoiceLinesEditor } from './InvoiceLinesEditor';
import { emptyEditorLine, type InvoiceEditorLine } from './invoiceEditorLines';

interface CreateStandaloneInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
}

const PAYER_TYPES: InvoicePayerPartyType[] = ['person', 'company', 'insurer'];

export function CreateStandaloneInvoiceDialog({ open, onClose }: CreateStandaloneInvoiceDialogProps) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();
  const createInvoice = useCreateStandaloneInvoice();
  const payerInputRef = useRef<HTMLInputElement | null>(null);

  const [partyType, setPartyType] = useState<InvoicePayerPartyType>('person');
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<InvoiceEditorLine[]>(() => [
    emptyEditorLine(DEFAULT_VAT_PERCENT, 'new-0'),
  ]);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);
  const enableSearch = open && debouncedSearch.trim().length >= 2;
  const personsQuery = usePersons(
    { q: debouncedSearch || undefined },
    { enabled: enableSearch && partyType === 'person' },
  );
  const companiesQuery = useCompanies(
    { q: debouncedSearch || undefined },
    { enabled: enableSearch && partyType !== 'person' },
  );
  const personOptions = personsQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const companyOptions = companiesQuery.data?.pages.flatMap((page) => page.data) ?? [];

  useEffect(() => {
    if (!open) return;
    setPartyType('person');
    setSelectedPerson(null);
    setSelectedCompany(null);
    setSearch('');
    setLines([emptyEditorLine(DEFAULT_VAT_PERCENT, 'new-0')]);
    setErrorKey(null);
  }, [open]);

  const selectedId =
    partyType === 'person' ? selectedPerson?.id ?? null : selectedCompany?.id ?? null;
  const payer = useMemo(
    () => buildStandaloneInvoicePayer(partyType, selectedId),
    [partyType, selectedId],
  );
  const isDirty = Boolean(selectedPerson || selectedCompany || lines.some((line) => line.description.trim()));
  const payerLabel =
    partyType === 'person'
      ? t('createStandalone.selectPerson')
      : partyType === 'insurer'
        ? t('createStandalone.selectInsurer')
        : t('createStandalone.selectCompany');

  const handlePartyTypeChange = (_event: unknown, value: InvoicePayerPartyType | null) => {
    if (!value) return;
    setPartyType(value);
    setSelectedPerson(null);
    setSelectedCompany(null);
    setSearch('');
    setErrorKey(null);
  };

  const handleSubmit = async () => {
    const validation = standaloneInvoiceValidationKey(payer, lines);
    if (validation || !payer) {
      setErrorKey(validation ?? 'createStandalone.payerRequired');
      payerInputRef.current?.focus();
      return;
    }
    setErrorKey(null);
    try {
      const created = await createInvoice.mutateAsync(buildStandaloneInvoiceBody(payer, lines));
      notify.success(t('createStandalone.success', { invoiceNumber: created.invoiceNumber }));
      onClose();
      navigate(`/invoices/${created.id}`);
    } catch (err) {
      setErrorKey(null);
      notify.error(tc('error'), translateCreateStandaloneError(err, t));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('createStandalone.title')}
      isDirty={isDirty}
      onSubmit={() => void handleSubmit()}
      submitLabel={t('createStandalone.submit')}
      isLoading={createInvoice.isPending}
      maxWidth="md"
    >
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('createStandalone.help')}
          </Typography>

          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={partyType}
            onChange={handlePartyTypeChange}
            aria-label={t('createStandalone.payerType')}
            data-testid="create-standalone-payer-type"
          >
            {PAYER_TYPES.map((type) => (
              <ToggleButton key={type} value={type}>
                {t(`createStandalone.party.${type}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {partyType === 'person' ? (
            <Autocomplete<Person>
              data-testid="create-standalone-payer-search"
              options={personOptions}
              loading={personsQuery.isLoading}
              value={selectedPerson}
              onChange={(_event, value) => {
                setSelectedPerson(value);
                setErrorKey(null);
              }}
              inputValue={search}
              onInputChange={(_event, value) => setSearch(value)}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`.trim()}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={payerInputRef}
                  label={payerLabel}
                  required
                  error={errorKey === 'createStandalone.payerRequired'}
                  helperText={
                    errorKey === 'createStandalone.payerRequired'
                      ? t('createStandalone.payerRequired')
                      : t('createStandalone.searchHint')
                  }
                />
              )}
              noOptionsText={enableSearch ? t('createStandalone.noPartiesFound') : t('createStandalone.searchHint')}
            />
          ) : (
            <Autocomplete<Company>
              data-testid="create-standalone-payer-search"
              options={companyOptions}
              loading={companiesQuery.isLoading}
              value={selectedCompany}
              onChange={(_event, value) => {
                setSelectedCompany(value);
                setErrorKey(null);
              }}
              inputValue={search}
              onInputChange={(_event, value) => setSearch(value)}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={payerInputRef}
                  label={payerLabel}
                  required
                  error={errorKey === 'createStandalone.payerRequired'}
                  helperText={
                    errorKey === 'createStandalone.payerRequired'
                      ? t('createStandalone.payerRequired')
                      : t('createStandalone.searchHint')
                  }
                />
              )}
              noOptionsText={enableSearch ? t('createStandalone.noPartiesFound') : t('createStandalone.searchHint')}
            />
          )}

          <Box>
            <InvoiceLinesEditor
              lines={lines}
              onChange={(next) => {
                setLines(next);
                setErrorKey(null);
              }}
              fallbackTaxPercent={DEFAULT_VAT_PERCENT}
            />
          </Box>

          {errorKey && errorKey !== 'createStandalone.payerRequired' && (
            <Alert severity="error" role="alert" data-testid="create-standalone-error">
              {t(errorKey)}
            </Alert>
          )}
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
